/**
 * Map toggle channel for Search Results Map Variant.
 *
 * Provides a single, stable publish/subscribe channel that decouples the
 * view-toggle UI (viewToggler.js) from the map plugins that react to it.
 *
 * Design rationale:
 * - A BehaviorSubject is kept private so callers can only push values through
 *   `setMapToggle`, not `.complete()` or `.error()` them.
 * - `mapToggle$` exposes only the read-only Observable projection so plugins
 *   can subscribe without being able to emit.
 * - BehaviorSubject(false) reflects the default state (list view on mobile);
 *   on desktop the map is always visible, so the initial `false` is immediately
 *   overridden by the combineLatest in index.js.
 *
 * @module searchResultsMap/services/mapToggle
 */

import { BehaviorSubject } from 'rxjs';

const mapToggleSubject$ = new BehaviorSubject(false);

/**
 * Observable stream of map toggle state.
 * Emits `true` when the user has toggled to map view, `false` for list view.
 * Replays the current value immediately to new subscribers (BehaviorSubject).
 *
 * @type {Observable<boolean>}
 */
export const mapToggle$ = mapToggleSubject$.asObservable();

/**
 * Signals that the map view has been toggled on or off.
 *
 * Call with `true` when the user switches to map view, `false` when switching
 * back to list view. Callers do not need to know anything about the map
 * feature — they just describe intent, and the map reacts.
 *
 * @param {boolean} isMapVisible
 */
export function signalMapToggle(isMapVisible) {
  mapToggleSubject$.next(isMapVisible);
}
